import {sortNumberAsc} from "../lib/langExtensions";
import {createSvgElement} from "../lib/domFunctions";
import {ContainerView} from "./Containers";

const PixelPerfect = .5;

export class ContainerTrackLine {
	static HORIZONTAL_LINE = 0;
	static VERTICAL_LINE = 1;
	static DIRECT_LINE = 2;

	readonly type: number // True, если линия вертикальная. False - если горизонтальная

	// main: number  // Координата X линии, если линия вертикальная. Иначе Y
	// sec1: number  // Координата y1 линии, если линия вертикальная. Иначе x1
	// sec2: number // Координата y2 линии, если линия вертикальная. Иначе x2
	markerStart?: string
	markerEnd?: string

	p1: { x: number, y: number }
	p2: { x: number, y: number }

	constructor(type: number, p1: { x: number, y: number }, p2: { x: number, y: number }, opts: { markerStart?: string, markerEnd?: string } = {}) {
		this.type = type

		this.p1 = p1;
		this.p2 = p2;
		this.markerStart = opts.markerStart;
		this.markerEnd = opts.markerEnd;
	}

	get vertical() {
		return this.type == ContainerTrackLine.VERTICAL_LINE
	}

	get direct() {
		return this.type == ContainerTrackLine.DIRECT_LINE
	}

	get start() {
		return this.p1;
	}

	get end() {
		return this.p2;
	}

	get main() {
		return this.type == ContainerTrackLine.VERTICAL_LINE ? this.p1.x : this.p1.y;
	}

	set main(n: number) {
		if (this.type == ContainerTrackLine.VERTICAL_LINE) {
			this.p1.x = this.p2.x = n;
		} else {
			this.p1.y = this.p2.y = n;
		}
	}

	set sec1(n: number) {
		if (this.type == ContainerTrackLine.VERTICAL_LINE) {
			this.p1.y = n;
		} else {
			this.p1.x = n;
		}
	}

	set sec2(n: number) {
		if (this.type == ContainerTrackLine.VERTICAL_LINE) {
			this.p2.y = n;
		} else {
			this.p2.x = n;
		}
	}

	get secondaryInterval() {
		const sec1 = this.type == ContainerTrackLine.VERTICAL_LINE ? this.p1.y : this.p1.x;
		const sec2 = this.type == ContainerTrackLine.VERTICAL_LINE ? this.p2.y : this.p2.x;
		return [sec1, sec2];
	}

	isSameSpan(line: ContainerTrackLine) {
		if (this.type != line.type) return false;

		const a = this.secondaryInterval.sort(sortNumberAsc)
		const b = line.secondaryInterval.sort(sortNumberAsc)
		return a[0] <= b[1] && b[0] <= a[1];
	}

	append(other: ContainerTrackLine) {
		if (this.type == other.type && ContainerTrackLine.equalPoints(this.end, other.start) && !this.markerEnd && !other.markerStart) {
			this.p2 = other.p2;
			this.markerEnd = other.markerEnd;
			return true;
		}
		return false;
	}

	toString() {
		const typeString = this.type == ContainerTrackLine.DIRECT_LINE ? 'D' : this.type == ContainerTrackLine.VERTICAL_LINE ? 'V' : 'H';
		return `${typeString}: ${JSON.stringify(this.start)} -> ${JSON.stringify(this.end)}`;
	}

	static equalPoints(a: { x: number, y: number }, b: { x: number, y: number }): boolean {
		return a.x == b.x && a.y == b.y;
	}

	static vertical(x: number, y1: number, y2: number, opts: { markerStart?: string, markerEnd?: string } = {}) {
		return new ContainerTrackLine(ContainerTrackLine.VERTICAL_LINE, {x, y: y1}, {x, y: y2}, opts);
	}

	static horizontal(y: number, x1: number, x2: number, opts: { markerStart?: string, markerEnd?: string } = {}) {
		return new ContainerTrackLine(ContainerTrackLine.HORIZONTAL_LINE, {x: x1, y}, {x: x2, y}, opts);
	}

	static direct(x: number, y: number, x2: number, y2: number, opts: { markerStart?: string, markerEnd?: string } = {}) {
		return new ContainerTrackLine(ContainerTrackLine.DIRECT_LINE, {x, y}, {x: x2, y: y2}, opts);
	}
}

export class ContainerPathTrack {
	readonly id: number;
	path: number[]

	svgLines: SVGElement[] = [];
	trackLines: ContainerTrackLine[] = [];

	constructor(id: number, path: number[]) {
		this.id = id;
		this.path = path;
	}

	clear() {
		if (this.svgLines.length) {
			this.svgLines.forEach(l => l.remove());
		}
		this.trackLines = [];
		this.svgLines = [];
	}

	reset(trackLines: ContainerTrackLine[], parent: SVGElement, colorIndex: number) {
		this.clear();
		if (trackLines.length == 0) {
			return;
		}

		interface LineSegment {
			d: string,
			class: string,
			markerStart?: string
			markerEnd?: string
		}

		function addPathSegment(out: LineSegment[], item: LineSegment) {
			const needNew = out.length == 0 || out[out.length - 1].markerEnd || item.markerStart;
			if (needNew) {
				out.push(item);
			} else {
				const last = out[out.length - 1];
				last.d += ' ' + item.d;
				last.markerEnd = item.markerEnd;
				last.class += ' ' + item.class;
			}
		}

		const svgList: LineSegment[] = [];

		// рисуем прямые линии
		for (let i = 0; i < trackLines.length; ++i) {
			const line = trackLines[i];
			if (line.direct) {
				addPathSegment(svgList, {class: '', d: `M ${line.start.x + PixelPerfect},${line.start.y + PixelPerfect} L ${line.end.x + PixelPerfect},${line.end.y + PixelPerfect}`, markerStart: line.markerStart, markerEnd: line.markerEnd})
			} else if (line.vertical) {
				addPathSegment(svgList, {class: '', d: `M ${line.start.x + PixelPerfect},${line.start.y + PixelPerfect} V ${line.end.y + PixelPerfect}`, markerStart: line.markerStart, markerEnd: line.markerEnd})
			} else {
				addPathSegment(svgList, {class: '', d: `M ${line.start.x + PixelPerfect},${line.start.y + PixelPerfect} H ${line.end.x + PixelPerfect}`, markerStart: line.markerStart, markerEnd: line.markerEnd})
			}
		}

		const svgLines = svgList.map((l, index, array) => {
			let lineClass = l.class.replace('#', `${colorIndex}`) + (l.markerStart || ' ') + (l.markerEnd || ' ');
			lineClass += ` svg-c-track svg-c-track-${colorIndex}`;
			if (index == array.length - 1) {
				lineClass += ` svg-c-track-${colorIndex}-end`;
			}
			const line = createSvgElement('path', {class: lineClass, d: l.d});
			parent.insertBefore(line, parent.firstElementChild)
			return line;
		});

		this.trackLines = trackLines;
		this.svgLines = svgLines;
	}
}

export interface ContainerTrackGenerator {
	makeTrackLines(track: ContainerPathTrack): ContainerTrackLine[];

	avoidIntersections(containerTracks: Map<number, ContainerPathTrack>, id: number, trackLines: ContainerTrackLine[]): void;
}
